'use client'

import { useState } from 'react'
import Link from 'next/link'
import { trpc } from '@/lib/trpc/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Plus, Loader2, LifeBuoy, Store } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import PublicContactForm from './PublicContactForm'
import { BRAND } from '@/components/marketing/MarketingChrome'

const STATUS_CONFIG = {
  open: { label: 'Open', variant: 'info' as const },
  pending: { label: 'Pending', variant: 'warning' as const },
  closed: { label: 'Closed', variant: 'default' as const },
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  bug: 'Bug report',
  billing: 'Billing',
  feature: 'Feature request',
  other: 'Other',
}

export default function SupportPage() {
  // These two are auth *probes*, not data loads: /support is a public page and a
  // 401 here is the normal signal to render the public contact form. They must
  // opt out of the provider's throwOnError default, which would otherwise send
  // every logged-out visitor to the global error boundary.
  const { data: me, isLoading: meLoading } = trpc.support.me.useQuery(undefined, {
    retry: false,
    throwOnError: false,
  })
  const { data: tickets, isLoading: ticketsLoading, error } = trpc.support.list.useQuery(undefined, {
    retry: false,
    throwOnError: false,
  })

  const isLoading = meLoading || ticketsLoading
  const isAuthenticated = !error && !!me

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: BRAND.indigo }} />
      </div>
    )
  }

  if (isAuthenticated && Array.isArray(tickets)) {
    return <EmbeddedSupportPage tickets={tickets} shopDomain={me.shopDomain} />
  }

  return <PublicSupportPage />
}

// ── Embedded (authenticated) view ─────────────────────────────────────

interface TicketItem {
  id: string
  subject: string
  status: string
  category: string
  contactEmail: string
  updatedAt: Date | string
  messages: Array<{ id: string; body: string; createdAt: Date | string }>
}

function EmbeddedSupportPage({ tickets, shopDomain }: { tickets: TicketItem[]; shopDomain: string }) {
  const [filter, setFilter] = useState<'all' | 'open' | 'pending' | 'closed'>('all')
  const filtered = tickets.filter((t) => filter === 'all' || t.status === filter)

  const tabs = [
    { key: 'all' as const, label: 'All' },
    { key: 'open' as const, label: 'Open' },
    { key: 'pending' as const, label: 'Pending' },
    { key: 'closed' as const, label: 'Closed' },
  ]

  return (
    <div className="px-6 py-8 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-2.5 bg-olive-100 border border-olive-200 rounded-xl px-4 py-3">
        <div className="h-7 w-7 bg-olive-700 rounded-lg flex items-center justify-center shrink-0">
          <Store className="h-3.5 w-3.5 text-olive-100" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-olive-900">
            Signed in as <span className="font-semibold">{shopDomain}</span>
          </p>
          <p className="text-xs text-olive-500">Your tickets are linked to this Shopify store</p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 bg-olive-700 text-olive-100 text-xs font-semibold px-2.5 py-1 rounded-full">
          <span className="h-1.5 w-1.5 rounded-full bg-olive-400 inline-block" />
          Connected
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-olive-900">Support tickets</h1>
          <p className="text-olive-500 text-sm mt-0.5">Get help from the YesPersonalized team</p>
        </div>
        <Link href="/support/new">
          <Button size="sm" className="bg-brand-coral hover:bg-brand-coral-dark">
            <Plus className="h-3.5 w-3.5" />
            New ticket
          </Button>
        </Link>
      </div>

      <div className="flex gap-1 bg-olive-100 border border-olive-200 p-1 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === tab.key
                ? 'bg-olive-800 text-white shadow-sm'
                : 'text-olive-600 hover:text-olive-900'
            }`}
          >
            {tab.label}
            {tab.key !== 'all' && (
              <span className={`ml-1.5 text-xs ${filter === tab.key ? 'text-olive-300' : 'text-olive-400'}`}>
                {tickets.filter((t) => t.status === tab.key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-olive-200">
          <div className="h-12 w-12 rounded-xl bg-olive-100 flex items-center justify-center mx-auto mb-4">
            <LifeBuoy className="h-6 w-6 text-olive-500" />
          </div>
          <h3 className="text-sm font-semibold text-olive-900 mb-1">
            {filter === 'all' ? 'No tickets yet' : `No ${filter} tickets`}
          </h3>
          <p className="text-sm text-olive-500 mb-6">
            {filter === 'all' ? "Need help? Open a ticket and we'll get back to you." : 'Try a different filter.'}
          </p>
          {filter === 'all' && (
            <Link href="/support/new">
              <Button size="sm" className="bg-brand-coral hover:bg-brand-coral-dark"><Plus className="h-3.5 w-3.5" />Open a ticket</Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((ticket) => {
            const cfg = STATUS_CONFIG[ticket.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.open
            const lastMessage = ticket.messages[0]
            return (
              <Link
                key={ticket.id}
                href={`/support/${ticket.id}`}
                className="block bg-white rounded-xl border border-olive-200 p-4 hover:border-olive-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-olive-400">#{ticket.id.slice(0, 8).toUpperCase()}</span>
                      <span className="text-xs text-olive-300">·</span>
                      <span className="text-xs text-olive-400">{CATEGORY_LABELS[ticket.category] ?? ticket.category}</span>
                    </div>
                    <p className="text-sm font-semibold text-olive-900 truncate group-hover:text-red-600 transition-colors">
                      {ticket.subject}
                    </p>
                    {lastMessage && (
                      <p className="text-sm text-olive-500 mt-0.5 truncate">{lastMessage.body}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge variant={cfg.variant}>{cfg.label}</Badge>
                    <span className="text-xs text-olive-400">
                      {formatDistanceToNow(new Date(ticket.updatedAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Public (editorial) view ───────────────────────────────────────────

function PublicSupportPage() {
  return (
    <>
      {/* Hero */}
      <section className="saas-hero" style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${BRAND.rule}` }}>
        <div className="saas-hero-grid" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
        <div className="max-w-[1000px] mx-auto px-5 sm:px-6 pt-20 pb-16 md:pt-24 md:pb-20 relative">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <span className="saas-eyebrow">Support</span>
              <h1 className="font-display" style={{
                fontWeight: 700,
                fontSize: 'clamp(40px, 8vw, 80px)',
                lineHeight: 0.98,
                color: BRAND.ink,
                marginTop: 18,
              }}>
                Write to us. We write back.
              </h1>
            </div>
            <div className="md:col-span-5">
              <p style={{ fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.6, color: BRAND.muted }}>
                Tickets from this form land in an inbox read by humans — usually within a working day, often faster. Reply to the confirmation email to add more detail.
              </p>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-0 mt-14" style={{ borderTop: `1px solid ${BRAND.rule}`, borderBottom: `1px solid ${BRAND.rule}` }}>
            {[
              { k: 'Typical reply', v: 'Within 24h' },
              { k: 'Answered by', v: 'A human' },
              { k: 'Reply via', v: 'Email' },
            ].map((b, i, arr) => (
              <div key={b.k} className="py-4 px-4" style={{ borderRight: i < arr.length - 1 ? `1px solid ${BRAND.rule}` : undefined }}>
                <div style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
                  color: BRAND.mutedSoft, marginBottom: 4,
                }}>{b.k}</div>
                <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.02em', color: BRAND.ink }}>{b.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form section */}
      <section style={{ background: '#fff', padding: 'clamp(64px, 9vw, 96px) 20px' }}>
        <div className="max-w-[1000px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <span className="saas-eyebrow">Open a ticket</span>
            <h2 className="font-display" style={{
              fontWeight: 700,
              fontSize: 'clamp(24px, 3vw, 32px)',
              lineHeight: 1.1,
              color: BRAND.ink,
              marginTop: 14, marginBottom: 14,
            }}>
              The more detail, the better the reply.
            </h2>
            <p style={{ fontSize: 14.5, color: BRAND.muted, lineHeight: 1.65, marginBottom: 20 }}>
              If something is broken, tell us what you were doing, what you expected, and what happened instead. Store URL and order IDs help enormously.
            </p>
            <div style={{ borderTop: `1px solid ${BRAND.rule}`, paddingTop: 16 }}>
              <div style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: BRAND.mutedSoft, marginBottom: 6,
              }}>Prefer email?</div>
              <a href="mailto:support@yespersonalized.com" style={{ fontSize: 14, fontWeight: 600, color: BRAND.ink }}>
                support@yespersonalized.com <span style={{ color: BRAND.indigo }}>→</span>
              </a>
            </div>
          </div>

          <div className="md:col-span-8">
            <PublicContactForm />
          </div>
        </div>
      </section>
    </>
  )
}
